import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Bug, Globe, Smartphone, Monitor, Shield, AlertTriangle, Play, Eye, Trash2, ArrowLeft, Loader2, Upload, CheckCircle2, XCircle, Clock, Target, BookOpen, Network, Settings2, Lock, KeyRound, Sparkles, FileJson } from 'lucide-react';
import { getProDashboard, getProScans, getPentestProfiles, getOWASPChecks, startWebDASTScan, startMobileScan, startDesktopScan, deleteProScan, parseAPISpec, SCAN_TYPES, SCAN_PROFILES, OWASP_TOP_10, SEVERITY_COLORS, MOBILE_PLATFORMS, DESKTOP_PLATFORMS } from '@/services/litesabrePro';
import { toast } from 'sonner';
import RulesDashboard from './RulesDashboard';
import CustomRulesManager from './CustomRulesManager';
import SubdomainFinder from '../osint/SubdomainFinder';
import AIPentestDashboard from './AIPentestDashboard';

const LiteSabreProPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine base path from current location
  const proBasePath = location.pathname.startsWith('/litesabre-pro') 
    ? '/litesabre-pro' 
    : '/grc/litesabre-pro';
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [scans, setScans] = useState([]);
  const [pentestProfiles, setPentestProfiles] = useState([]);
  const [owaspChecks, setOwaspChecks] = useState([]);
  const [showScanModal, setShowScanModal] = useState(false);
  const [scanType, setScanType] = useState('web_dast');
  const [scanning, setScanning] = useState(false);
  const [activeMainTab, setActiveMainTab] = useState('scanner');
  
  // Web DAST form
  // Web DAST form
  const [webForm, setWebForm] = useState({
    name: '',
    url: '',
    profile: 'standard',
    auth_type: 'none',
    api_spec_url: '',
    include_subdomains: false,
    // Credentialed scan
    login_url: '',
    username_field: '',
    password_field: '',
    submit_selector: '',
    username: '',
    password: '',
    bearer_token: '',
    cookie_header: '',
    // API spec upload
    api_spec_file: null,
    api_spec_parsed: null,
    // AI-assisted VAPT (BYOK)
    ai_enabled: false,
    ai_provider: 'anthropic',
    ai_model: 'claude-sonnet-5',
    ai_api_key: '',
  });
  
  // Mobile form
  const [mobileForm, setMobileForm] = useState({
    name: '',
    platform: 'android',
    file: null
  });
  
  // Desktop form
  const [desktopForm, setDesktopForm] = useState({
    name: '',
    platform: 'windows',
    file: null
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [dashboardData, scansData, profilesData, owaspData] = await Promise.all([
        getProDashboard(),
        getProScans(),
        getPentestProfiles(),
        getOWASPChecks()
      ]);
      setDashboard(dashboardData);
      setScans(scansData);
      setPentestProfiles(profilesData);
      setOwaspChecks(owaspData.checks || []);
    } catch (error) {
      toast.error('Failed to load scanner data');
    } finally {
      setLoading(false);
    }
  };

  const handleStartWebScan = async () => {
    if (!webForm.name || !webForm.url) {
      toast.error('Name and URL are required');
      return;
    }
    
    // Ensure URL has protocol
    let targetUrl = webForm.url.trim();
    if (!targetUrl.match(/^https?:\/\//i)) {
      if (targetUrl.match(/^[\w.-]+\.\w{2,}/)) {
        targetUrl = 'https://' + targetUrl;
      } else {
        toast.error('Please enter a valid URL or domain');
        return;
      }
    }
    
    try {
      setScanning(true);

      // Build auth config
      let authPayload = null;
      if (webForm.auth_type === 'form') {
        authPayload = {
          auth_type: 'form',
          login_url: webForm.login_url,
          username_field: webForm.username_field,
          password_field: webForm.password_field,
          username: webForm.username,
          password: webForm.password,
          custom_headers: webForm.submit_selector
            ? { 'X-Submit-Selector': webForm.submit_selector } : null,
        };
      } else if (webForm.auth_type === 'basic') {
        authPayload = { auth_type: 'basic', username: webForm.username,
                        password: webForm.password };
      } else if (webForm.auth_type === 'bearer') {
        authPayload = { auth_type: 'bearer', bearer_token: webForm.bearer_token };
      } else if (webForm.auth_type === 'cookie') {
        authPayload = { auth_type: 'cookie',
                        custom_headers: { Cookie: webForm.cookie_header } };
      }

      const aiPayload = webForm.ai_enabled ? {
        enabled: true,
        provider: webForm.ai_provider,
        model: webForm.ai_model,
        api_key: webForm.ai_api_key || null,  // empty = use Emergent Universal Key
      } : null;

      const apiSpecUpload = webForm.api_spec_parsed ? {
        spec_type: webForm.api_spec_parsed.spec_type || 'openapi_json',
        filename: webForm.api_spec_parsed.filename || null,
        content: webForm.api_spec_parsed._raw || '',
      } : null;

      await startWebDASTScan({
        name: webForm.name,
        target: {
          url: targetUrl,
          scan_scope: 'domain',
          include_subdomains: webForm.include_subdomains,
          max_depth: 5,
          max_pages: 100
        },
        profile: webForm.profile,
        auth: authPayload,
        api_spec_url: webForm.api_spec_url || null,
        api_spec_upload: apiSpecUpload,
        ai_assisted: aiPayload,
      });
      toast.success('DAST scan started');
      setShowScanModal(false);
      setWebForm({ name: '', url: '', profile: 'standard', auth_type: 'none',
                   api_spec_url: '', include_subdomains: false,
                   login_url: '', username_field: '', password_field: '',
                   submit_selector: '', username: '', password: '',
                   bearer_token: '', cookie_header: '',
                   api_spec_file: null, api_spec_parsed: null,
                   ai_enabled: false, ai_provider: 'anthropic',
                   ai_model: 'claude-sonnet-5', ai_api_key: '' });
      await loadData();
    } catch (error) {
      toast.error('Failed to start scan');
    } finally {
      setScanning(false);
    }
  };

  const handleStartMobileScan = async () => {
    if (!mobileForm.file) {
      toast.error('Please select a file');
      return;
    }
    
    try {
      setScanning(true);
      const formData = new FormData();
      formData.append('file', mobileForm.file);
      formData.append('platform', mobileForm.platform);
      formData.append('scan_type', 'static');
      if (mobileForm.name) formData.append('name', mobileForm.name);
      
      await startMobileScan(formData);
      toast.success('Mobile scan started');
      setShowScanModal(false);
      await loadData();
    } catch (error) {
      toast.error('Failed to start mobile scan');
    } finally {
      setScanning(false);
    }
  };

  const handleStartDesktopScan = async () => {
    if (!desktopForm.file) {
      toast.error('Please select a file');
      return;
    }
    
    const MAX_SIZE = 500 * 1024 * 1024; // 500MB
    if (desktopForm.file.size > MAX_SIZE) {
      toast.error(`File too large (${Math.round(desktopForm.file.size / (1024*1024))}MB). Maximum allowed size is 500MB.`);
      return;
    }
    
    try {
      setScanning(true);
      const formData = new FormData();
      formData.append('file', desktopForm.file);
      formData.append('platform', desktopForm.platform);
      formData.append('scan_type', 'static');
      if (desktopForm.name) formData.append('name', desktopForm.name);
      
      await startDesktopScan(formData);
      toast.success('Desktop scan started');
      setShowScanModal(false);
      await loadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Failed to start desktop scan');
    } finally {
      setScanning(false);
    }
  };

  const handleDeleteScan = async (scanId) => {
    if (!confirm('Delete this scan?')) return;
    try {
      await deleteProScan(scanId);
      toast.success('Scan deleted');
      await loadData();
    } catch (error) {
      toast.error('Failed to delete scan');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'failed': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'running': return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
      default: return <Clock className="w-4 h-4 text-yellow-500" />;
    }
  };

  const getScanTypeIcon = (type) => {
    if (type?.includes('web')) return <Globe className="w-4 h-4" />;
    if (type?.includes('mobile')) return <Smartphone className="w-4 h-4" />;
    if (type?.includes('desktop')) return <Monitor className="w-4 h-4" />;
    return <Bug className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`${proBasePath}`)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Bug className="w-7 h-7 text-red-600" />
              LiteSabre Pro Scanner
            </h1>
            <p className="text-gray-500">Automated penetration testing for Web, Mobile, and Desktop apps</p>
          </div>
        </div>
        {activeMainTab === 'scanner' && (
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => navigate('/grc/integrations')}
              className="border-gray-300"
            >
              <Settings2 className="w-4 h-4 mr-2" />
              Integrations
            </Button>
            <Button onClick={() => setShowScanModal(true)} className="bg-red-600 hover:bg-red-700">
              <Target className="w-4 h-4 mr-2" />
              New Pentest Scan
            </Button>
          </div>
        )}
      </div>

      {/* Main Tabs */}
      <Tabs value={activeMainTab} onValueChange={setActiveMainTab}>
        <TabsList className="grid grid-cols-6 w-full max-w-[900px]">
          <TabsTrigger value="scanner" className="flex items-center gap-2">
            <Bug className="w-4 h-4" />
            Scanner
          </TabsTrigger>
          <TabsTrigger value="ai-pentest" className="flex items-center gap-2" data-testid="ai-pentest-tab">
            <Target className="w-4 h-4" />
            AI Pentest
          </TabsTrigger>
          <TabsTrigger value="rules" className="flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            Security Rules
          </TabsTrigger>
          <TabsTrigger value="custom-rules" className="flex items-center gap-2">
            <Settings2 className="w-4 h-4" />
            Custom Rules
          </TabsTrigger>
          <TabsTrigger value="subdomains" className="flex items-center gap-2">
            <Network className="w-4 h-4" />
            Subdomains
          </TabsTrigger>
          <TabsTrigger value="owasp" className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            OWASP
          </TabsTrigger>
        </TabsList>

        {/* AI Pentest Tab */}
        <TabsContent value="ai-pentest" className="mt-6">
          <AIPentestDashboard />
        </TabsContent>

        <TabsContent value="scanner" className="space-y-6 mt-6">
          {/* Dashboard Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
              <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm">Total Scans</p>
                <p className="text-3xl font-bold">{dashboard?.total_scans || 0}</p>
              </div>
              <Bug className="w-10 h-10 text-red-200" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <p className="text-gray-500 text-sm mb-2">By Scan Type</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(dashboard?.scans_by_type || {}).map(([type, count]) => (
                <Badge key={type} variant="outline">
                  {getScanTypeIcon(type)} {type.replace('_', ' ')}: {count}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <p className="text-gray-500 text-sm mb-2">Findings by Severity</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(dashboard?.findings_by_severity || {}).map(([sev, count]) => (
                <Badge key={sev} className={`${SEVERITY_COLORS[sev]?.badge || 'bg-gray-400'} text-white`}>
                  {sev}: {count}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <p className="text-gray-500 text-sm mb-2">OWASP Coverage</p>
            <div className="flex flex-wrap gap-1">
              {Object.entries(dashboard?.owasp_coverage || {}).slice(0, 5).map(([id, count]) => (
                <Badge key={id} variant="outline" className="text-xs">
                  {id}: {count}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="cursor-pointer hover:border-blue-300 transition-colors" onClick={() => { setScanType('web_dast'); setShowScanModal(true); }}>
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Globe className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold">Web App DAST</h3>
              <p className="text-sm text-gray-500">OWASP Top 10 testing, SQL injection, XSS</p>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:border-green-300 transition-colors" onClick={() => { setScanType('mobile'); setShowScanModal(true); }}>
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <Smartphone className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold">Mobile App Scan</h3>
              <p className="text-sm text-gray-500">APK/IPA analysis, secrets, permissions</p>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:border-purple-300 transition-colors" onClick={() => { setScanType('desktop'); setShowScanModal(true); }}>
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Monitor className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold">Desktop App Scan</h3>
              <p className="text-sm text-gray-500">PE/ELF/Mach-O analysis, security flags</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Scans */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Scans</CardTitle>
          <CardDescription>View and manage your penetration test scans</CardDescription>
        </CardHeader>
        <CardContent>
          {scans.length === 0 ? (
            <div className="text-center py-12">
              <Bug className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No scans yet</p>
              <p className="text-sm text-gray-400">Start a new pentest scan to find vulnerabilities</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Findings</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {scans.map((scan) => (
                  <TableRow key={scan.id}>
                    <TableCell className="font-medium">{scan.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="flex items-center gap-1 w-fit">
                        {getScanTypeIcon(scan.scan_type)}
                        {scan.scan_type?.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate">{scan.target}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(scan.status)}
                        <span className="capitalize">{scan.status}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {scan.summary ? (
                        <div className="flex gap-1">
                          {scan.summary.critical_count > 0 && (
                            <Badge className="bg-red-600">{scan.summary.critical_count}C</Badge>
                          )}
                          {scan.summary.high_count > 0 && (
                            <Badge className="bg-orange-500">{scan.summary.high_count}H</Badge>
                          )}
                          {scan.summary.medium_count > 0 && (
                            <Badge className="bg-yellow-500 text-black">{scan.summary.medium_count}M</Badge>
                          )}
                        </div>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {new Date(scan.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => navigate(`${proBasePath}/scans/${scan.id}`)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteScan(scan.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="rules">
          <RulesDashboard />
        </TabsContent>

        <TabsContent value="custom-rules">
          <CustomRulesManager />
        </TabsContent>

        <TabsContent value="subdomains">
          <SubdomainFinder />
        </TabsContent>

        <TabsContent value="owasp">
          {/* OWASP Top 10 Reference */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                OWASP Top 10 (2021) Coverage
              </CardTitle>
              <CardDescription>
                The scanner tests for all OWASP Top 10 vulnerabilities with comprehensive payloads
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {OWASP_TOP_10.map((item) => (
                  <div key={item.id} className={`p-3 rounded-lg ${item.color} text-white`}>
                    <p className="font-bold text-sm">{item.id}</p>
                    <p className="text-xs opacity-90">{item.name}</p>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 space-y-4">
                <h4 className="font-medium">Tests Performed per Category</h4>
                {owaspChecks.map((check) => (
                  <div key={check.id} className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <h5 className="font-medium">{check.id}: {check.name}</h5>
                      </div>
                      <Badge variant="outline">{check.tests?.length || 0} tests</Badge>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {check.tests?.slice(0, 5).map((test, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">{test}</Badge>
                      ))}
                      {check.tests?.length > 5 && (
                        <Badge variant="outline" className="text-xs">+{check.tests.length - 5} more</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Scan Modal */}
      <Dialog open={showScanModal} onOpenChange={setShowScanModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Start New Pentest Scan</DialogTitle>
          </DialogHeader>
          
          <Tabs value={scanType} onValueChange={setScanType}>
            <TabsList className="grid grid-cols-3">
              <TabsTrigger value="web_dast" className="flex items-center gap-1">
                <Globe className="w-4 h-4" /> Web
              </TabsTrigger>
              <TabsTrigger value="mobile" className="flex items-center gap-1">
                <Smartphone className="w-4 h-4" /> Mobile
              </TabsTrigger>
              <TabsTrigger value="desktop" className="flex items-center gap-1">
                <Monitor className="w-4 h-4" /> Desktop
              </TabsTrigger>
            </TabsList>

            <TabsContent value="web_dast" className="space-y-4 mt-4">
              <div>
                <Label>Scan Name</Label>
                <Input
                  value={webForm.name}
                  onChange={(e) => setWebForm({ ...webForm, name: e.target.value })}
                  placeholder="My Web App Scan"
                />
              </div>
              <div>
                <Label>Target URL</Label>
                <div className="relative">
                  <Input
                    value={webForm.url}
                    onChange={(e) => {
                      let url = e.target.value.trim();
                      // Auto-detect and suggest protocol
                      setWebForm({ ...webForm, url });
                    }}
                    onBlur={(e) => {
                      let url = e.target.value.trim();
                      // Auto-prefix http/https if missing
                      if (url && !url.match(/^https?:\/\//i)) {
                        // Check if it looks like a domain
                        if (url.match(/^[\w.-]+\.\w{2,}/)) {
                          // Default to https for security
                          url = 'https://' + url;
                          setWebForm({ ...webForm, url });
                        }
                      }
                    }}
                    placeholder="example.com (auto-detects https://)"
                    className="pr-20"
                  />
                  {webForm.url && !webForm.url.match(/^https?:\/\//i) && webForm.url.match(/[\w.-]+\.\w{2,}/) && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                      → https://
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Enter domain or full URL. Protocol (https://) will be auto-added if missing.
                </p>
              </div>
              <div>
                <Label>Scan Profile</Label>
                <Select value={webForm.profile} onValueChange={(v) => setWebForm({ ...webForm, profile: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SCAN_PROFILES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        {p.label} - {p.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>API Spec URL (optional)</Label>
                <Input
                  value={webForm.api_spec_url}
                  onChange={(e) => setWebForm({ ...webForm, api_spec_url: e.target.value })}
                  placeholder="https://api.example.com/openapi.json or /scalar/v1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Supports OpenAPI/Swagger JSON, Scalar, Redoc, or Swagger UI pages
                </p>
              </div>

              {/* API Spec Upload (OpenAPI / Swagger / Postman) */}
              <div className="border-t pt-3">
                <div className="flex items-center gap-2 mb-2">
                  <FileJson className="w-4 h-4 text-indigo-600" />
                  <Label className="text-sm font-semibold">Upload API Spec (optional)</Label>
                  {webForm.api_spec_parsed && (
                    <Badge variant="outline" className="text-[10px]">
                      {webForm.api_spec_parsed.endpoint_count} endpoints
                    </Badge>
                  )}
                </div>
                <Input
                  type="file"
                  accept=".json,.yaml,.yml"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      const text = await file.text();
                      const parsed = await parseAPISpec(file);
                      setWebForm({
                        ...webForm,
                        api_spec_file: file,
                        api_spec_parsed: { ...parsed, _raw: text }
                      });
                      toast.success(`Parsed ${parsed.endpoint_count} endpoints from ${parsed.spec_type}`);
                    } catch (err) {
                      console.error(err);
                      toast.error('Failed to parse API spec');
                    }
                  }}
                  data-testid="api-spec-upload"
                />
                <p className="text-xs text-gray-500 mt-1">
                  OpenAPI 3 / Swagger 2 (JSON or YAML) or Postman Collection v2.1
                </p>
              </div>

              {/* Credentialed Scan */}
              <div className="border-t pt-3">
                <div className="flex items-center gap-2 mb-2">
                  <Lock className="w-4 h-4 text-emerald-600" />
                  <Label className="text-sm font-semibold">Credentialed Scan (authenticated)</Label>
                </div>
                <Select value={webForm.auth_type} onValueChange={(v) => setWebForm({ ...webForm, auth_type: v })}>
                  <SelectTrigger data-testid="auth-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No authentication</SelectItem>
                    <SelectItem value="form">Form login (username/password)</SelectItem>
                    <SelectItem value="basic">HTTP Basic auth</SelectItem>
                    <SelectItem value="bearer">Bearer / JWT token</SelectItem>
                    <SelectItem value="cookie">Raw Cookie header</SelectItem>
                  </SelectContent>
                </Select>

                {webForm.auth_type === 'form' && (
                  <div className="mt-3 space-y-2 bg-emerald-50/40 border border-emerald-100 rounded p-3">
                    <Input placeholder="Login URL (e.g., https://app.example.com/login)"
                           value={webForm.login_url}
                           onChange={(e) => setWebForm({ ...webForm, login_url: e.target.value })}
                           data-testid="login-url" />
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="Username CSS selector (#email)"
                             value={webForm.username_field}
                             onChange={(e) => setWebForm({ ...webForm, username_field: e.target.value })} />
                      <Input placeholder="Password CSS selector (#password)"
                             value={webForm.password_field}
                             onChange={(e) => setWebForm({ ...webForm, password_field: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="Username"
                             value={webForm.username}
                             onChange={(e) => setWebForm({ ...webForm, username: e.target.value })}
                             data-testid="cred-username" />
                      <Input type="password" placeholder="Password"
                             value={webForm.password}
                             onChange={(e) => setWebForm({ ...webForm, password: e.target.value })}
                             data-testid="cred-password" />
                    </div>
                    <Input placeholder="Submit button selector (optional, e.g., button[type=submit])"
                           value={webForm.submit_selector}
                           onChange={(e) => setWebForm({ ...webForm, submit_selector: e.target.value })} />
                  </div>
                )}
                {webForm.auth_type === 'basic' && (
                  <div className="mt-3 grid grid-cols-2 gap-2 bg-emerald-50/40 border border-emerald-100 rounded p-3">
                    <Input placeholder="Username"
                           value={webForm.username}
                           onChange={(e) => setWebForm({ ...webForm, username: e.target.value })} />
                    <Input type="password" placeholder="Password"
                           value={webForm.password}
                           onChange={(e) => setWebForm({ ...webForm, password: e.target.value })} />
                  </div>
                )}
                {webForm.auth_type === 'bearer' && (
                  <Textarea className="mt-3" placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6..."
                    value={webForm.bearer_token}
                    onChange={(e) => setWebForm({ ...webForm, bearer_token: e.target.value })}
                    rows={2} data-testid="bearer-token" />
                )}
                {webForm.auth_type === 'cookie' && (
                  <Textarea className="mt-3"
                    placeholder="session=abc123; XSRF-TOKEN=..."
                    value={webForm.cookie_header}
                    onChange={(e) => setWebForm({ ...webForm, cookie_header: e.target.value })}
                    rows={2} data-testid="cookie-header" />
                )}
              </div>

              {/* AI-Assisted VAPT (BYOK) */}
              <div className="border-t pt-3">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-purple-600" />
                  <Label className="text-sm font-semibold">AI-Assisted VAPT</Label>
                  <input type="checkbox"
                    checked={webForm.ai_enabled}
                    onChange={(e) => setWebForm({ ...webForm, ai_enabled: e.target.checked })}
                    className="ml-2 w-4 h-4 text-purple-600 rounded"
                    data-testid="ai-enable-toggle" />
                  <span className="text-xs text-gray-500">Enable</span>
                </div>
                {webForm.ai_enabled && (
                  <div className="space-y-2 bg-purple-50/40 border border-purple-100 rounded p-3">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Provider</Label>
                        <Select value={webForm.ai_provider}
                          onValueChange={(v) => {
                            const defaultModel = v === 'anthropic' ? 'claude-sonnet-5'
                              : v === 'gemini' ? 'gemini-3.1-pro' : 'gpt-5.6-terra';
                            setWebForm({ ...webForm, ai_provider: v, ai_model: defaultModel });
                          }}>
                          <SelectTrigger data-testid="ai-provider-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="anthropic">Claude (Anthropic)</SelectItem>
                            <SelectItem value="gemini">Gemini (Google)</SelectItem>
                            <SelectItem value="openai">GPT (OpenAI)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Model</Label>
                        <Select value={webForm.ai_model}
                          onValueChange={(v) => setWebForm({ ...webForm, ai_model: v })}>
                          <SelectTrigger data-testid="ai-model-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {webForm.ai_provider === 'anthropic' && <>
                              <SelectItem value="claude-sonnet-5">Claude Sonnet 5</SelectItem>
                              <SelectItem value="claude-opus-4.8">Claude Opus 4.8</SelectItem>
                              <SelectItem value="claude-haiku-4.5">Claude Haiku 4.5</SelectItem>
                            </>}
                            {webForm.ai_provider === 'gemini' && <>
                              <SelectItem value="gemini-3.1-pro">Gemini 3.1 Pro</SelectItem>
                              <SelectItem value="gemini-3-flash">Gemini 3 Flash</SelectItem>
                              <SelectItem value="gemini-3.5-flash">Gemini 3.5 Flash</SelectItem>
                            </>}
                            {webForm.ai_provider === 'openai' && <>
                              <SelectItem value="gpt-5.6-terra">GPT 5.6 Terra</SelectItem>
                              <SelectItem value="gpt-5.6-sol">GPT 5.6 Sol</SelectItem>
                              <SelectItem value="gpt-5.6-luna">GPT 5.6 Luna</SelectItem>
                            </>}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs flex items-center gap-1">
                        <KeyRound className="w-3 h-3" /> Your API Key (BYOK — optional)
                      </Label>
                      <Input type="password" placeholder="sk-... or Anthropic/Gemini key. Leave blank to use Emergent Universal Key."
                        value={webForm.ai_api_key}
                        onChange={(e) => setWebForm({ ...webForm, ai_api_key: e.target.value })}
                        data-testid="ai-api-key" />
                    </div>
                    <p className="text-xs text-gray-500">
                      AI reasoning augments pattern-matched findings with business-impact analysis, PoC drafting,
                      and remediation suggestions.
                    </p>
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="include_subdomains"
                  checked={webForm.include_subdomains}
                  onChange={(e) => setWebForm({ ...webForm, include_subdomains: e.target.checked })}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                />
                <Label htmlFor="include_subdomains" className="cursor-pointer">
                  Include Subdomains
                  <span className="text-xs text-gray-500 ml-2">(Enumerate and scan discovered subdomains)</span>
                </Label>
              </div>
              <Button onClick={handleStartWebScan} disabled={scanning} className="w-full bg-blue-600 hover:bg-blue-700">
                {scanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                Start DAST Scan
              </Button>
            </TabsContent>

            <TabsContent value="mobile" className="space-y-4 mt-4">
              <div>
                <Label>Scan Name (optional)</Label>
                <Input
                  value={mobileForm.name}
                  onChange={(e) => setMobileForm({ ...mobileForm, name: e.target.value })}
                  placeholder="My Mobile App Scan"
                />
              </div>
              <div>
                <Label>Platform</Label>
                <Select value={mobileForm.platform} onValueChange={(v) => setMobileForm({ ...mobileForm, platform: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MOBILE_PLATFORMS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        {p.icon} {p.label} ({p.extensions.join(', ')})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Upload File</Label>
                <div className="border-2 border-dashed rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept=".apk,.ipa"
                    onChange={(e) => setMobileForm({ ...mobileForm, file: e.target.files[0] })}
                    className="hidden"
                    id="mobile-file"
                  />
                  <label htmlFor="mobile-file" className="cursor-pointer">
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">
                      {mobileForm.file ? mobileForm.file.name : 'Click to upload APK or IPA'}
                    </p>
                  </label>
                </div>
              </div>
              <Button onClick={handleStartMobileScan} disabled={scanning} className="w-full bg-green-600 hover:bg-green-700">
                {scanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                Start Mobile Scan
              </Button>
            </TabsContent>

            <TabsContent value="desktop" className="space-y-4 mt-4">
              <div>
                <Label>Scan Name (optional)</Label>
                <Input
                  value={desktopForm.name}
                  onChange={(e) => setDesktopForm({ ...desktopForm, name: e.target.value })}
                  placeholder="My Desktop App Scan"
                />
              </div>
              <div>
                <Label>Platform</Label>
                <Select value={desktopForm.platform} onValueChange={(v) => setDesktopForm({ ...desktopForm, platform: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DESKTOP_PLATFORMS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        {p.icon} {p.label} ({p.extensions.join(', ')})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Upload File</Label>
                <div className="border-2 border-dashed rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept=".exe,.dll,.msi,.app,.dmg,.pkg,.so,.deb,.rpm"
                    onChange={(e) => setDesktopForm({ ...desktopForm, file: e.target.files[0] })}
                    className="hidden"
                    id="desktop-file"
                  />
                  <label htmlFor="desktop-file" className="cursor-pointer">
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">
                      {desktopForm.file ? desktopForm.file.name : 'Click to upload executable'}
                    </p>
                  </label>
                </div>
              </div>
              <Button onClick={handleStartDesktopScan} disabled={scanning} className="w-full bg-purple-600 hover:bg-purple-700">
                {scanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                Start Desktop Scan
              </Button>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LiteSabreProPage;
